import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'semantic-ui-react';
import styled from 'styled-components';

import { AdminRole, Affiliation } from '@/types/affiliation';
import { Admin } from '@/types/admin';
import { getFullName } from '@/utils/name';

import { Information } from './Information';
import { AssignRole } from './AssignRole';
import { DeleteMembership } from './DeleteMembership';
import { MembershipHint } from './MembershipHint';
import './index.css';

type Props = {
  children?: never;
  affiliations: Affiliation[];
  gloplaId: string;
  me: Admin;
  hasManagerInOrganization: boolean;
  isManager: boolean;
  onClickDelete: (adminId: number) => void;
  onClickUpdateRole: (adminId: number, role: AdminRole) => void;
};

export const MembershipList: React.FC<Props> = (props) => {
  const {
    affiliations,
    gloplaId,
    me,
    hasManagerInOrganization,
    isManager,
    onClickDelete,
    onClickUpdateRole,
  } = props;
  const { t } = useTranslation();

  return (
    <div className="membership-list">
      <MembershipHint />
      {affiliations.map((affiliation) => {
        const { admin, role } = affiliation;
        const isMe = admin.id === me.id;

        return (
          <Card
            fluid={true}
            data-test="admin"
            key={admin.id + affiliation.role}
          >
            <Card.Content className="membership-list-content">
              <div>
                <div className="membership-list-info">
                  <div>
                    <Card.Header>
                      {!admin.activatedAt && (
                        <span>{t('text.membership.index.notActivated')}</span>
                      )}
                      {getFullName(admin.firstName, admin.lastName)}
                    </Card.Header>
                    <Card.Meta className="membership-list-kana-name">
                      {getFullName(admin.kanaFirstName, admin.kanaLastName)}
                    </Card.Meta>
                  </div>
                  <Card.Meta
                    data-test="admin-email"
                    className={`membership-list-email`}
                  >
                    {admin.email}
                  </Card.Meta>
                </div>
                {isManager ? (
                  <div
                    className={`membership-list-control ${
                      isMe && 'membership-list-control-me'
                    }`}
                  >
                    <div>
                      {!isMe ? (
                        <AssignRole
                          affiliation={affiliation}
                          onClickUpdateRole={onClickUpdateRole}
                        />
                      ) : (
                        <span>
                          {t(`models.affiliation.roleType.${role}.name`)}
                        </span>
                      )}
                    </div>
                    {!isMe && (
                      <div>
                        <DeleteMembership {...{ affiliation, onClickDelete }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <MemberRole>
                    {t(`models.affiliation.roleType.${role}.name`)}
                  </MemberRole>
                )}
              </div>
            </Card.Content>
          </Card>
        );
      })}
      {!hasManagerInOrganization && <Information gloplaId={gloplaId} />}
    </div>
  );
};

const MemberRole = styled.span`
  margin-left: auto;
`;
